import api, { handleApiError } from './api';
import { Auction, Bid, BidStatus, AuctionFilters, BidHistoryResponse } from '../types/auction';
import { OneOnOneServiceCategory } from '../utils/auctionLabels';

type NumericLike = number | string | null | undefined;

const toNumber = (value: NumericLike): number => {
  if (value === null || value === undefined) {
    return 0;
  }
  if (typeof value === 'number') {
    return value;
  }
  const parsed = Number(value);
  return Number.isNaN(parsed) ? 0 : parsed;
};

const hasValue = (value: NumericLike): value is number | string => value !== null && value !== undefined;

type RawAuction = Omit<Auction, 'starting_price' | 'min_bid' | 'bid_step' | 'bid_cost' | 'buy_now_price' | 'reserve_price' | 'reveal_target_price' | 'total_bids' | 'unique_bidders' | 'duration_minutes' | 'duration_value'> & {
  starting_price: NumericLike;
  min_bid?: NumericLike;
  bid_step?: NumericLike;
  bid_cost?: NumericLike;
  buy_now_price?: NumericLike;
  reserve_price?: NumericLike;
  reveal_target_price?: NumericLike;
  total_bids: NumericLike;
  unique_bidders: NumericLike;
  duration_minutes?: NumericLike;
  duration_value?: NumericLike;
};

type RawBid = Omit<Bid, 'bid_amount' | 'bid_cost'> & {
  bid_amount: NumericLike;
  bid_cost: NumericLike;
};

type RawBidHistoryResponse = Omit<BidHistoryResponse, 'bids'> & {
  bids: RawBid[];
};

type RawBidStatus = Omit<BidStatus, 'bid_amount' | 'bid_cost'> & {
  bid_amount: NumericLike;
  bid_cost?: NumericLike;
};

const normalizeAuction = (auction: RawAuction): Auction => ({
  ...auction,
  starting_price: toNumber(auction.starting_price),
  min_bid: hasValue(auction.min_bid) ? toNumber(auction.min_bid) : undefined,
  bid_step: hasValue(auction.bid_step) ? toNumber(auction.bid_step) : undefined,
  bid_cost: hasValue(auction.bid_cost) ? toNumber(auction.bid_cost) : undefined,
  buy_now_price: hasValue(auction.buy_now_price) ? toNumber(auction.buy_now_price) : undefined,
  reserve_price: hasValue(auction.reserve_price) ? toNumber(auction.reserve_price) : undefined,
  reveal_target_price: hasValue(auction.reveal_target_price) ? toNumber(auction.reveal_target_price) : undefined,
  total_bids: toNumber(auction.total_bids),
  unique_bidders: toNumber(auction.unique_bidders),
  duration_minutes: hasValue(auction.duration_minutes) ? toNumber(auction.duration_minutes) : undefined,
  duration_value: hasValue(auction.duration_value) ? toNumber(auction.duration_value) : undefined,
});

const normalizeBid = (bid: RawBid): Bid => ({
  ...bid,
  bid_amount: toNumber(bid.bid_amount),
  bid_cost: toNumber(bid.bid_cost),
});

const normalizeBidStatus = (status: RawBidStatus): BidStatus => ({
  ...status,
  bid_amount: toNumber(status.bid_amount),
  bid_cost: status.bid_cost !== undefined ? toNumber(status.bid_cost) : undefined,
});

export const auctionService = {
  /**
   * Get list of auctions with filters
   */
  async getAuctions(filters?: AuctionFilters): Promise<Auction[]> {
    try {
      // Convert page to offset for backend API
      const params: any = { ...filters };
      if (params.page !== undefined) {
        const page = params.page || 1;
        const limit = params.limit || 20;
        params.offset = (page - 1) * limit;
        delete params.page; // Remove page param as backend expects offset
      }

      const response = await api.get<{ auctions: RawAuction[]; total: number; limit: number; offset: number }>('/auctions/', { params });
      // API returns {auctions: [], total: 0, ...} so extract the auctions array
      const auctions = response.data.auctions || [];
      return auctions.map((auction) => normalizeAuction(auction));
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  },

  /**
   * Get my auctions (auctions created by current user)
   */
  async getMyAuctions(status?: string, limit = 50, offset = 0): Promise<Auction[]> {
    try {
      const params: any = { limit, offset };
      if (status) params.status = status;
      const response = await api.get<{ auctions: RawAuction[]; total: number }>('/auctions/my/auctions', { params });
      const auctions = response.data.auctions || [];
      return auctions.map((auction) => normalizeAuction(auction));
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  },

  /**
   * Get auction by ID
   */
  async getAuctionById(id: number): Promise<Auction> {
    try {
      const response = await api.get<RawAuction>(`/auctions/${id}`);
      return normalizeAuction(response.data);
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  },

  /**
   * Place a bid on an auction
   */
  async placeBid(auctionId: number, amount: number): Promise<Bid> {
    try {
      const response = await api.post<RawBid>(`/auctions/${auctionId}/bid`, {
        bid_amount: amount,
      });
      return normalizeBid(response.data);
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  },

  /**
   * Get bids for an auction
   */
  async getAuctionBids(auctionId: number): Promise<Bid[]> {
    try {
      const response = await api.get<RawBidHistoryResponse>(`/auctions/${auctionId}/bids`);
      const bids = response.data.bids || [];
      return bids.map((bid) => normalizeBid(bid));
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  },

  /**
   * Get user's bid status for an auction
   */
  async getBidStatus(auctionId: number): Promise<BidStatus | null> {
    try {
      const response = await api.get<RawBidStatus>(`/auctions/${auctionId}/my-bid-status`);
      const normalized = normalizeBidStatus(response.data);
      if (normalized.status === 'no_bid') {
        return null;
      }
      return normalized;
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  },

  /**
   * Get user's bids
   */
  async getMyBids(): Promise<Bid[]> {
    try {
      const response = await api.get<Bid[]>('/auctions/my-bids');
      return response.data;
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  },

  /**
   * Create auction (for models)
   */
  async createAuction(data: Partial<Auction>): Promise<Auction> {
    try {
      const response = await api.post<Auction>('/auctions/', data);
      return response.data;
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  },

  /**
   * Create dating date auction (for models)
   */
  async createDatingDateAuction(data: any): Promise<Auction> {
    try {
      const response = await api.post<Auction>('/dating-dates/', data);
      return response.data;
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  },

  /**
   * Create silent date auction (for models)
   */
  async createSilentDateAuction(data: any): Promise<Auction> {
    try {
      const response = await api.post<Auction>('/silent-date-auctions/', data);
      return response.data;
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  },

  /**
   * Get approved Personal Service categories for the current tenant
   */
  async getOneOnOneServiceCategories(): Promise<OneOnOneServiceCategory[]> {
    try {
      const response = await api.get<OneOnOneServiceCategory[]>('/one-on-one-categories');
      return response.data || [];
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  },

  /**
   * Update auction (for models)
   */
  async updateAuction(id: number, data: Partial<Auction>): Promise<Auction> {
    try {
      const response = await api.put<Auction>(`/auctions/${id}`, data);
      return response.data;
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  },

  /**
   * Cancel auction (for models)
   */
  async cancelAuction(id: number): Promise<{ success: boolean; message: string }> {
    try {
      const response = await api.delete(`/auctions/${id}`);
      return response.data;
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  },

  /**
   * End auction (owner only) — declares winners or refunds bids
   */
  async endAuction(id: number): Promise<{ message: string; auction_id: number }> {
    try {
      const response = await api.post(`/auctions/${id}/end`);
      return response.data;
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  },

  /**
   * Upload auction image
   */
  async uploadImage(file: File): Promise<{ image_url: string; filename: string }> {
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await api.post<{ image_url: string; filename: string }>(
        '/auctions/media/upload-image',
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      return response.data;
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  },

  /**
   * Upload auction video
   */
  async uploadVideo(file: File): Promise<{ video_url: string; filename: string }> {
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await api.post<{ video_url: string; filename: string }>(
        '/auctions/media/upload-video',
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      return response.data;
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  },

  /**
   * Upload auction digital prize file
   */
  async uploadDigitalPrize(file: File): Promise<{ digital_url: string; filename: string }> {
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await api.post<{ digital_url: string; filename: string }>(
        '/auctions/media/upload-digital-prize',
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      return response.data;
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  },

  /**
   * Get winners for an auction
   */
  async getAuctionWinners(auctionId: number): Promise<any> {
    try {
      const response = await api.get(`/auctions/${auctionId}/winners`);
      return response.data;
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  },

  /**
   * Get my wins
   */
  async getMyWins(status?: string, limit = 20, offset = 0): Promise<any> {
    try {
      const params: any = { limit, offset };
      if (status) params.status = status;
      const response = await api.get('/auctions/my-wins', { params });
      return response.data;
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  },

  /**
   * Accept a win
   */
  async acceptWin(auctionId: number): Promise<{ success: boolean; message: string }> {
    try {
      const response = await api.post(`/auctions/${auctionId}/winner/accept`);
      return response.data;
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  },

  /**
   * Decline a win
   */
  async declineWin(auctionId: number, reason?: string): Promise<{ success: boolean; message: string }> {
    try {
      const params = reason ? { reason } : {};
      const response = await api.post(`/auctions/${auctionId}/winner/decline`, null, { params });
      return response.data;
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  },

  /**
   * Submit winner phone number (dating date auctions)
   */
  async submitWinnerPhone(auctionId: number, phone: string): Promise<{ message: string }> {
    try {
      const response = await api.post(`/auctions/${auctionId}/winner/submit-phone`, null, {
        params: { phone },
      });
      return response.data;
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  },

  /**
   * Verify meeting code (creator side — releases earnings)
   */
  async verifyMeetingCode(auctionId: number, code: string): Promise<{ message: string; payout_released: boolean }> {
    try {
      const response = await api.post(`/auctions/${auctionId}/verify-meeting-code`, null, {
        params: { code: code.toUpperCase() },
      });
      return response.data;
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  },

  /**
   * Submit delivery address
   */
  async submitDeliveryAddress(auctionId: number, addressData: any): Promise<any> {
    try {
      const response = await api.post(`/delivery/${auctionId}/submit-address`, addressData);
      return response.data;
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  },

  /**
   * Get all deliveries for an auction (Admin/Model)
   */
  async getAuctionDeliveries(auctionId: number): Promise<any[]> {
    try {
      const response = await api.get(`/delivery/${auctionId}/delivery-status`);
      // The backend returns a single delivery object or a pending status
      // We wrap it in an array to match the frontend expectation in some components
      return response.data ? [response.data] : [];
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  },

  /**
   * Winner confirms they received the physical delivery — releases escrow
   */
  async confirmDeliveryReceived(auctionId: number): Promise<any> {
    try {
      const response = await api.post(`/delivery/${auctionId}/confirm-received`);
      return response.data;
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  },

  /**
   * Winner raises a dispute for undelivered item
   */
  async raiseDeliveryDispute(auctionId: number, reason: string): Promise<any> {
    try {
      const response = await api.post(`/delivery/${auctionId}/raise-dispute?reason=${encodeURIComponent(reason)}`);
      return response.data;
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  },

  /**
   * Get delivery status from creator's perspective
   */
  async getCreatorDeliveryStatus(auctionId: number): Promise<any> {
    try {
      const response = await api.get(`/delivery/${auctionId}/creator-delivery-status`);
      return response.data;
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  },

  /**
   * Get delivery status
   */
  async getDeliveryStatus(auctionId: number): Promise<any> {
    try {
      const response = await api.get(`/delivery/${auctionId}/delivery-status`);
      return response.data;
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  },

  /**
   * Get my deliveries
   */
  async getMyDeliveries(status?: string, limit = 20, offset = 0): Promise<any> {
    try {
      const params: any = { limit, offset };
      if (status) params.status = status;
      const response = await api.get('/delivery/my-deliveries', { params });
      return response.data;
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  },

  /**
   * Add tracking information (Admin/Model)
   */
  async addTrackingInfo(deliveryId: number, trackingNumber: string, courier: string): Promise<any> {
    try {
      const response = await api.post(`/delivery/${deliveryId}/add-tracking`, null, {
        params: { tracking_number: trackingNumber, courier }
      });
      return response.data;
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  },

  /**
   * Mark as delivered (Admin/Model)
   */
  async markDelivered(deliveryId: number): Promise<any> {
    try {
      const response = await api.post(`/delivery/${deliveryId}/mark-delivered`);
      return response.data;
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  },

  /**
   * Get pending deliveries (Admin)
   */
  async getPendingDeliveries(limit = 50, offset = 0): Promise<any> {
    try {
      const response = await api.get('/delivery/admin/pending-deliveries', {
        params: { limit, offset }
      });
      return response.data;
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  },

  /**
   * Get model's pending deliveries
   */
  async getModelPendingDeliveries(limit = 50, offset = 0): Promise<any> {
    try {
      const response = await api.get('/delivery/model/pending-deliveries', {
        params: { limit, offset }
      });
      return response.data;
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  },

  /**
   * Get dating date session for an auction
   */
  async getDatingDateSession(auctionId: number): Promise<any> {
    try {
      const response = await api.get(`/dating-dates/${auctionId}/session`);
      return response.data;
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  },

  /**
   * Schedule a dating date
   */
  async scheduleDate(auctionId: number, data: { scheduled_time: string; age_verified: boolean; terms_accepted: boolean }): Promise<any> {
    try {
      const response = await api.post(`/dating-dates/${auctionId}/schedule`, data);
      return response.data;
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  },

  /**
   * Get my dating dates
   */
  async getMyDates(): Promise<any> {
    try {
      const response = await api.get('/dating-dates/my-dates');
      return response.data;
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  },

  /**
   * Verify date code (for models)
   */
  async verifyDateCode(auctionId: number, dateCode: string): Promise<any> {
    try {
      const response = await api.post(`/dating-dates/${auctionId}/verify-code`, { date_code: dateCode });
      return response.data;
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  }
};
